const React = require('react');
const { useEffect, useRef, useState, useMemo } = React;
const { Link } = require('react-router-dom');
const { default: Styled } = require('styled-components');
const { default: Button } = require('@mui/material/Button');
const { context } = require('app-context');
const { v4: uuid } = require('uuid');
const { useNerpy } = require('components/Nerpy/useNerpy');
const Fonts = require('styles/fonts.json');
const Trivia = require('components/Nerpy/Trivia');
const NerpyAvatar = require('components/Nerpy/Avatar');
const ConversationStarters = require('routes/app/components/Chat/ConversationStarters');
const StarterQuestions = require('components/Nerpy/starterQuestions');
const AboutNerpy = require('components/Nerpy/About');

const ChatPresentation = require('routes/app/components/Chat/Presentation');
const { default: Classes } = require('routes/app/components/Chat/styles.scss');
const { default: Paper } = require('@mui/material/Paper');
const { default: Collapse } = require('@mui/material/Collapse');
const { default: ArrowBack } = require('@mui/icons-material/ArrowBack');
const { default: SendIcon } = require('@mui/icons-material/Send');
const { default: IconButton } = require('@mui/material/IconButton');
const { useAppSelector } = require('utils/redux');
const Avatar = require('components/Gamification/WeeklyAvatar');
const { getSizedImageUrl } = require('utils/image');
const { runProfanityCheck } = require('utils/profanityFilter');
const { isSendKey } = require('utils/chatKeyboard');
const InappropriateLanguageDialog = require('routes/app/components/Chat/InappropriateLanguageDialog');

const { useChatProfileDisclosure } = require('hooks/useChatProfileDisclosure');

const internals = {};

module.exports = function NerpyPage() {

    const { scope, assistant, loading } = useNerpy();
    const { Chat, Page } = internals;

    if (!assistant) {
        return <Page><Link to='/app/messaging'>Back to Connections</Link><p role='status'>{loading ? 'Loading your companion…' : 'Your companion is not available for this profile.'}</p></Page>;
    }

    return <Chat key={scope} assistant={assistant} />;
};

internals.Chat = function Chat({ assistant }) {

    const name = assistant.name || 'Nerpy';
    const starterQuestions = useMemo(() => StarterQuestions.forName(name), [name]);
    const { Page, Conversation, Composer, Details, GameTools, AnswerText, CompanionTitle } = internals;
    const { IconBackButton, IconCollapseButton, HeaderExpandIcon, HeaderTextWrapper, HeaderTextAvatarLockup, ChatInput, ChatWrapper } = ChatPresentation;
    const user = useAppSelector(context.selectors.all.getCurrentUserObject);
    const schoolId = useAppSelector(context.selectors.all.getCurrentSchoolId);
    const useProfanityFilter = useAppSelector(context.selectors.all.getShouldUseProfanityFilter);
    const [blockedWords, setBlockedWords] = useState(null);
    const { collapsed, setCollapsed } = useChatProfileDisclosure({ viewerId: user?.id, schoolId, peerId: 'nerpy' });
    const expanded = !collapsed;
    const end = useRef(null);
    const input = useRef(null);
    const persistent = assistant.history === '30-days';
    const [loaded, setLoaded] = useState(!persistent);
    const [messages, setMessages] = useState(() => !persistent && assistant.welcome ? [{ text: assistant.welcome.text, sender: 'Nerpy' }] : []);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const conversationStarted = hasStarted || messages.some((message) => message.sender === 'You');
    const [error, setError] = useState('');
    const active = useRef(true);
    const requestInFlight = useRef(false);
    const retry = useRef(null);
    const limit = assistant.maxMessageLength || 2000;

    useEffect(() => {

        active.current = true;

        if (persistent) {
            context.api.nearpeer.nerpy.conversation().then((data) => {

                if (active.current) {
                    setMessages(data.messages);
                    setLoaded(true);
                    context.api.nearpeer.nerpy.read().then(() => {

                        if (active.current) {
                            context.redux.hooks.dispatch(context.actions.dataFetching.fetchNotifications());
                        }
                    }).catch(() => {});
                }
            }).catch(() => {

                if (active.current) {
                    setError(`Your conversation could not be loaded. Please reopen ${name} to try again.`);
                }
            });
        }

        return () => { active.current = false; };
    }, []);

    useEffect(() => { end.current?.scrollIntoView?.({ block: 'end' }); }, [messages.length, sending]);

    const send = async (event) => {

        event.preventDefault();
        const text = draft.trim();

        if (!loaded || !text || text.length > limit || requestInFlight.current) {
            return;
        }

        if (useProfanityFilter) {
            const { passesCheck, badPhrases } = runProfanityCheck(text);
            if (!passesCheck || badPhrases.length > 0) {
                setBlockedWords(badPhrases);
                return;
            }
        }

        requestInFlight.current = true;
        setHasStarted(true);
        setSending(true);
        setError('');

        try {
            if (persistent && retry.current?.text !== text) {
                retry.current = { text, requestId: uuid() };
            }

            const reply = await context.api.nearpeer.nerpy.message(text, ...(persistent ? [retry.current.requestId] : []));

            if (reply?.preview !== true || typeof reply.text !== 'string') {
                throw Object.assign(new Error('Invalid companion response'), { invalidNerpyResponse: true });
            }

            if (active.current) {
                setMessages((current) => [...current, { text, sender: 'You' }, { text: reply.text, sender: 'Nerpy', sources: reply.sources || [], resources: reply.resources || [], ...(reply.outreach ? { outreach: reply.outreach } : {}) }]);
                setDraft('');
                window.requestAnimationFrame(() => input.current?.focus());
                retry.current = null;
            }
        }
        catch (failure) {
            if (active.current) {
                const status = Number(failure?.response?.status || failure?.statusCode || failure?.status) || 0;
                if (persistent && retry.current && (status === 0 || failure?.invalidNerpyResponse)) {
                    try {
                        const saved = await context.api.nearpeer.nerpy.conversation();
                        const matching = saved.messages.filter((message) => message.requestId === retry.current?.requestId);
                        if (active.current && matching.some((message) => message.sender === 'You' && message.text === text) && matching.some((message) => message.sender === 'Nerpy')) {
                            setMessages(saved.messages);
                            setDraft('');
                            retry.current = null;
                            window.requestAnimationFrame(() => input.current?.focus());
                            return;
                        }
                    }
                    catch {
                        // Keep the same request ID for a safe, explicit retry.
                    }
                }
                if (!active.current) {
                    return;
                }
                const temporaryFailure = status >= 500;
                if (temporaryFailure) {
                    // The API permanently marks a failed provider request. A retry
                    // therefore needs a fresh request ID, while 409 remains protected.
                    retry.current = null;
                }
                const responseMessage = failure?.response?.data?.message;
                setError(failure?.invalidNerpyResponse ? `${name} returned an incomplete response. Retry to retrieve the saved reply.`
                    : status === 429 ? `${name} has reached today’s request limit. Please try again tomorrow.`
                    : status === 409 ? 'This request is already processing or could not finish. Wait and retry, or edit your message to start again.'
                        : temporaryFailure ? `${name} could not complete that request right now. Please try again.`
                        : status === 400 ? (responseMessage || 'That message could not be accepted. Please edit it and try again.')
                            : status === 0 ? `I couldn’t confirm whether ${name} received that. Retry to check without sending it twice.`
                                : 'Your message could not be sent. Please try again.');
            }
        }
        finally {
            if (active.current) {
                requestInFlight.current = false;
                setSending(false);
            }
        }
    };

    const answerCheckIn = async (id, answer) => {

        if (requestInFlight.current) {
            return;
        }

        requestInFlight.current = true;
        setSending(true);
        setError('');
        try {
            await context.api.nearpeer.nerpy.answerCheckIn(id, answer);
            const data = await context.api.nearpeer.nerpy.conversation();
            if (active.current) {
                setMessages(data.messages);
            }
        }
        catch {
            if (active.current) {
                setError('Your response could not be confirmed. Try again, or contact your school directly for support.');
            }
        }
        finally {
            if (active.current) {
                requestInFlight.current = false;
                setSending(false);
            }
        }
    };

    return <Page>
        <Paper className={Classes.nameWrapper} elevation={0}>
            <IconBackButton component={Link} to='/app/messaging' aria-label='Back to Connections' size='large'><ArrowBack /></IconBackButton>
            <HeaderTextWrapper><HeaderTextAvatarLockup><NerpyAvatar name={name} animated /><CompanionTitle>{name} — your companion</CompanionTitle></HeaderTextAvatarLockup></HeaderTextWrapper>
            <IconCollapseButton onClick={() => setCollapsed(expanded)} aria-label={`Show or hide ${name} profile`} aria-expanded={expanded} aria-controls='nerpy-profile'>
                <HeaderExpandIcon $headerIsCollapsed={!expanded} />
            </IconCollapseButton>
            <Collapse in={expanded} timeout='auto' unmountOnExit><Details id='nerpy-profile'><AboutNerpy name={name} expanded={expanded} /></Details></Collapse>
        </Paper>
        <Conversation role='log' aria-label={`Conversation with ${name}`} aria-live='polite'>
            {messages.map((message, index) => <div key={index} className={`${Classes.chatItemWrapper} ${message.sender === 'You' ? Classes.right : Classes.left}`}>
                <div className={Classes.talkBubble}>
                    <div className={Classes.avatarWrapper}>{message.sender === 'Nerpy' ? <NerpyAvatar name={name} /> : <Avatar userId={user?.id} src={getSizedImageUrl(user?.croppedPicture, 100)} alt='Your profile' size={44} />}</div>
                    <div className={Classes.chatItemInfo}><strong>{message.sender === 'Nerpy' ? name : message.sender}</strong></div>
                    <div className={Classes.talkText}>{message.sender === 'Nerpy' ? <AnswerText message={message} /> : <p>{message.text}</p>}
                    {message.outreach?.kind === 'check-in' && !message.outreach.answer && message.outreach.accepting && <div><Button variant='contained' disabled={sending} onClick={() => answerCheckIn(message.outreach.id, 'yes')}>Yes, please</Button> <Button disabled={sending} onClick={() => answerCheckIn(message.outreach.id, 'no')}>No, thanks</Button></div>}</div>
                </div>
            </div>)}
            {sending && <p role='status'>{name} is replying…</p>}
            <div ref={end} />
        </Conversation>
        {assistant.triviaEnabled && <GameTools><Trivia name={name} onReturn={() => input.current?.focus()} /></GameTools>}
        {!!error && <p role='alert'>{error} {draft.trim() && <Button size='small' onClick={() => send({ preventDefault: () => {} })} disabled={sending}>Retry</Button>}</p>}
        <Composer onSubmit={send}>
            <ChatWrapper>
                <ChatInput inputRef={input} placeholder='Type a message…' variant='standard' value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (isSendKey(event)) { send(event); } }} multiline maxRows={4} fullWidth disabled={sending || !loaded} inputProps={{ maxLength: limit, 'aria-label': `Message ${name}` }} InputProps={{ disableUnderline: true }} />
                <IconButton type='submit' aria-label={sending ? 'Sending…' : 'Send'} disabled={sending || !loaded || !draft.trim() || draft.length > limit}><SendIcon /></IconButton>
            </ChatWrapper>
            <ConversationStarters key={conversationStarted ? 'ongoing' : 'initial'} conversationStarters={starterQuestions} defaultExpanded={!conversationStarted} disabled={sending || !loaded} onItemClick={(text) => { setDraft(text); input.current?.focus(); }} />
        </Composer>
        <InappropriateLanguageDialog open={blockedWords !== null} words={blockedWords || []} onClose={() => setBlockedWords(null)} />
    </Page>;
};

internals.Page = Styled.section`
    position: absolute; left: 50%; transform: translateX(-50%); height: 100%;
    box-sizing: border-box; width: 100%; max-width: 600px; margin: 0 auto;
    display: flex; flex-direction: column; overflow: hidden;
    font-family: ${Fonts.copyFont}; color: ${({ theme }) => theme.palette?.text?.primary || 'inherit'};
    > .${Classes.nameWrapper} { flex-shrink: 0; }
    small { font-size: 12px; font-weight: 400; }
    @media (max-width: 600px) { max-width: 400px; }
`;
internals.Conversation = Styled.div`
    flex: 1; min-height: 0; overflow-y: auto; padding: 12px 0; overflow-wrap: anywhere;
    .${Classes.talkText} { white-space: pre-wrap; }
    .${Classes.talkBubble} { box-sizing: border-box; }
    .${Classes.avatarWrapper} img { width: 44px; height: 44px; }
    ul { padding-left: 20px; font-size: 14px; }
`;
internals.Composer = Styled.form`flex-shrink: 0; background: white;`;
internals.Details = Styled.div`padding: 8px 16px; max-height: 32vh; overflow-y: auto; border-top: 1px solid rgba(18,39,79,0.09); font-size: 14px; h3 { font-size: 16px; margin: 8px 0; } p { margin: 8px 0; }`;
internals.GameTools = Styled.div`flex: 0 1 auto; min-height: 0; max-height: 40vh; overflow-y: auto; padding: 4px 16px; font-size: 14px;`;

internals.CompanionTitle = Styled(ChatPresentation.HeaderText)`font-size: 18px; line-height: 24px; white-space: normal;`;

internals.AnswerText = function AnswerText({ message }) {

    const { Sources } = internals;
    const text = message.text.replace('This demo covers Nearpeer help and a small set of CSUN source notes. ', '').replace(/^(?:CSUN information|Nearpeer help):\s*/, '');
    const phrase = 'official CSUN page';
    const resources = message.resources || [];
    const inline = resources.length === 1 && text.includes(phrase) && internals.sourceHref(resources[0]?.url);
    const index = inline ? text.indexOf(phrase) : -1;

    return <><p>{inline ? <>{internals.linkProductPages(text.slice(0, index))}<a href={inline} target='_blank' rel='noopener noreferrer'>{phrase}</a>{internals.linkProductPages(text.slice(index + phrase.length))}</> : internals.linkProductPages(text)}</p>
        <Sources sources={message.sources} resources={inline ? [] : resources} /></>;
};

internals.linkProductPages = (text) => {

    const destinations = { connections: '/app/messaging', preferences: '/app/preferences#nerpy', 'edit interests': '/app/profile/edit/interests' };
    return text.split(/(\bConnections\b|\bPreferences\b|\bEdit Interests\b|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi).map((part, index) => {

        const path = destinations[part.toLowerCase()];
        if (path) {
            return <Link key={index} to={path}>{part}</Link>;
        }

        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(part) ? <a key={index} href={`mailto:${part}`}>{part}</a> : part;
    });
};

internals.Sources = function Sources({ sources, resources }) {

    const safe = (Array.isArray(sources) ? sources : []).filter((source) => !source?.url?.startsWith('/app/') && internals.sourceHref(source?.url));

    const official = (Array.isArray(resources) ? resources : []).filter((source) => !source?.url?.startsWith('/app/') && internals.sourceHref(source?.url));

    if (!safe.length && !official.length) {
        return null;
    }

    return <div aria-label='Official information'>
        {[...safe, ...official].filter((source, index, all) => all.findIndex((item) => item.url === source.url) === index).map((source, index) => <p key={`${source.id}:${index}`}>
            See <a href={source.url} target='_blank' rel='noopener noreferrer'>{source.title || 'official information'}</a>.
        </p>)}
    </div>;
};

internals.sourceHref = (value) => {

    if (['/app/messaging', '/app/profile/edit/interests'].includes(value)) {
        return value;
    }

    if (value === 'https://988lifeline.org?oai_link_source=model_response_hotline') {
        return value;
    }

    try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password && !url.port &&
            ['www.csun.edu', 'w2.csun.edu', 'catalog.csun.edu', 'library.csun.edu'].includes(url.hostname) ? value : null;
    }
    catch {
        return null;
    }
};
